// In-memory cache to avoid repeated API calls
const translationCache = new Map();

// Track failed translations to avoid repeated retries
const failedTranslations = new Set();

// Mobile: Limit cache size to prevent memory issues
const MAX_CACHE_SIZE = isMobileDevice() ? 500 : 2000; // Mobile: Smaller cache
const MAX_FAILED_SIZE = isMobileDevice() ? 100 : 500; // Mobile: Smaller failed cache

/**
 * Clean up cache if it exceeds size limit (mobile optimization)
 * This function is called conditionally based on device type
 */
function cleanupCache() {
 if (translationCache.size > MAX_CACHE_SIZE) {
 // Remove oldest entries (first 20%)
 const entriesToRemove = Math.floor(MAX_CACHE_SIZE * 0.2);
 const keys = Array.from(translationCache.keys());
 for (let i = 0; i < entriesToRemove; i++) {
 translationCache.delete(keys[i]);
 }
 }
 
 if (failedTranslations.size > MAX_FAILED_SIZE) {
 // Clear oldest failed translations
 const entriesToRemove = Math.floor(MAX_FAILED_SIZE * 0.2);
 const failedArray = Array.from(failedTranslations);
 for (let i = 0; i < entriesToRemove; i++) {
 failedTranslations.delete(failedArray[i]);
 }
 }
}

/**
 * Detect if browser is Microsoft Edge
 * @returns {boolean} True if Edge browser
 */
function isEdgeBrowser() {
 if (typeof navigator ==='undefined') return false;
 const userAgent = navigator.userAgent.toLowerCase();
 return userAgent.includes('edg/') || userAgent.includes('edgios') || userAgent.includes('edge/');
}

/**
 * Detect if running on mobile device
 * @returns {boolean} True if mobile device
 */
function isMobileDevice() {
 if (typeof navigator ==='undefined') return false;
 const ua = navigator.userAgent.toLowerCase();
 const mobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
 const isSmallScreen = typeof window !=='undefined'&& 
 window.matchMedia && 
 window.matchMedia('(max-width: 768px)').matches;
 const hasTouch ='ontouchstart'in window || navigator.maxTouchPoints > 0;
 return mobileUA || (isSmallScreen && hasTouch);
}

/**
 * Check if device has low performance
 * @returns {boolean} True if low performance device
 */
function isLowPerformanceDevice() {
 if (typeof navigator ==='undefined') return false;
 const cores = navigator.hardwareConcurrency || 2;
 const memory = navigator.deviceMemory || 4;
 return cores < 4 || memory < 2;
}

const isEdge = isEdgeBrowser();
const isMobile = isMobileDevice();
const isLowPerf = isLowPerformanceDevice();

/**
 * Check if device is online
 * @returns {boolean} True if online, false otherwise
 */
function isOnline() {
 if (typeof navigator !=='undefined'&&'onLine'in navigator) {
 return navigator.onLine;
 }
 return true; // Assume online if can't detect
}

/**
 * Generate cache key from text and language pair
 * @param {string} text - Text to translate
 * @param {string} sourceLanguage - Source language code
 * @param {string} targetLanguage - Target language code
 * @returns {string} Cache key
 */
function getCacheKey(text, sourceLanguage, targetLanguage) {
 return`${sourceLanguage}|${targetLanguage}|${text}`;
}

/**
 * Sleep/delay utility for retry mechanism
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise} Promise that resolves after delay
 */
function sleep(ms) {
 return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Translate text using Google Translate public endpoint with retry mechanism
 * @param {string} text - Text to translate
 * @param {string} sourceLanguage - Source language code (default:"auto")
 * @param {string} targetLanguage - Target language code (default:"en")
 * @param {number} maxRetries - Maximum number of retry attempts (default: 3)
 * @returns {Promise<string>} Translated text
 */
async function translateText(text, sourceLanguage ="auto", targetLanguage ="en", maxRetries = 3) {
 // Handle empty or null text
 if (!text || typeof text !=="string"|| text.trim() ==="") {
 return text ||"";
 }

 // If target language is the same as source (and source is not auto), return original
 if (sourceLanguage !=="auto"&& sourceLanguage === targetLanguage) {
 return text;
 }

 // Check if device is online
 if (!isOnline()) {
 console.warn("Translation skipped: Device is offline");
 return text;
 }

 // Check cache first
 const cacheKey = getCacheKey(text, sourceLanguage, targetLanguage);
 if (translationCache.has(cacheKey)) {
 return translationCache.get(cacheKey);
 }

 // Check if this translation has failed before (avoid repeated failures)
 if (failedTranslations.has(cacheKey)) {
 console.warn("Translation skipped: Previous attempt failed for this text");
 return text;
 }

 // Mobile/Low performance: Fewer retries to save battery and data
 const effectiveMaxRetries = (isMobile || isLowPerf) ? Math.min(maxRetries, 2) : maxRetries;
 
 // Retry mechanism with exponential backoff
 let lastError = null;
 for (let attempt = 0; attempt < effectiveMaxRetries; attempt++) {
 try {
 // Build Google Translate URL
 const url =`https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLanguage}&tl=${targetLanguage}&dt=t&q=${encodeURIComponent(text)}`;

 // Fetch translation with timeout - reduced for faster response
 // Reduced timeouts for faster failure detection and retry
 let timeoutMs = 5000; // Reduced from 10000 for faster response
 if (isMobile || isLowPerf) {
 timeoutMs = 8000; // Mobile: Reduced from 20000 for faster response
 } else if (isEdge) {
 timeoutMs = 6000; // Edge: Reduced from 15000
 }
 const controller = new AbortController();
 const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

 try {
 // Edge browser: More explicit fetch options for better CORS handling
 const fetchOptions = {
 signal: controller.signal,
 mode:'cors',
 cache:'no-cache',
 credentials:'omit', // Edge: Explicitly omit credentials for CORS
 referrerPolicy:'no-referrer'// Edge: Prevent referrer issues
 };

 // Edge browser: Add additional headers if needed
 if (isEdge) {
 fetchOptions.headers = {'Accept':'application/json','Accept-Language':'*'};
 }

 const response = await fetch(url, fetchOptions);

 clearTimeout(timeoutId);

 if (!response.ok) {
 // Handle rate limiting (429) and server errors (5xx)
 if (response.status === 429) {
 // Rate limited - wait longer before retry
 const waitTime = Math.min(1000 * Math.pow(2, attempt), 5000);
 console.warn(`Translation rate limited. Waiting ${waitTime}ms before retry ${attempt + 1}/${maxRetries}`);
 if (attempt < maxRetries - 1) {
 await sleep(waitTime);
 continue;
 }
 }
 throw new Error(`Translation failed: ${response.status} ${response.statusText}`);
 }

 // Parse response
 const data = await response.json();

 // Extract translated text from response
 // Response format: [[["translated text","original text", null, null, 0]], null,"en"]
 let translated = text; // Fallback to original
 if (Array.isArray(data) && data[0] && Array.isArray(data[0])) {
 const firstResult = data[0][0];
 if (Array.isArray(firstResult) && firstResult[0]) {
 translated = firstResult[0];
 }
 }

 // Validate translation result
 if (!translated || translated.trim() ===""|| translated === text) {
 // If translation is empty or same as original, might be an error
 if (attempt < maxRetries - 1) {
 await sleep(500 * (attempt + 1)); // Exponential backoff
 continue;
 }
 }

 // Cache the result
 translationCache.set(cacheKey, translated);
 
 // Mobile: Periodically clean up cache
 if (isMobile || isLowPerf) {
 if (translationCache.size % 50 === 0) {
 cleanupCache();
 }
 }

 return translated;
 } catch (fetchError) {
 clearTimeout(timeoutId);
 throw fetchError;
 }
 } catch (error) {
 lastError = error;
 
 // Log error with context
 console.warn(`Translation attempt ${attempt + 1}/${maxRetries} failed:`, {
 error: error.message,
 text: text.substring(0, 50) + (text.length > 50 ?'...':''),
 sourceLanguage,
 targetLanguage,
 online: isOnline()
 });

 // Don't retry on certain errors (CORS, network errors)
 if (error.name ==='AbortError'|| 
 error.message.includes('Failed to fetch') || 
 error.message.includes('NetworkError') ||
 error.message.includes('CORS')) {
 // For network/CORS errors, wait a bit and retry if attempts remain
 if (attempt < maxRetries - 1) {
 const waitTime = Math.min(1000 * Math.pow(2, attempt), 5000);
 await sleep(waitTime);
 // Check if device came back online
 if (!isOnline()) {
 console.warn("Translation aborted: Device went offline");
 break;
 }
 continue;
 }
 }

 // For other errors, retry with exponential backoff
 if (attempt < maxRetries - 1) {
 const waitTime = Math.min(500 * Math.pow(2, attempt), 3000);
 await sleep(waitTime);
 continue;
 }
 }
 }

 // All retries failed
 console.error("Translation failed after all retries:", {
 text: text.substring(0, 50) + (text.length > 50 ?'...':''),
 error: lastError?.message,
 sourceLanguage,
 targetLanguage
 });

 // Mark as failed to avoid repeated attempts
 failedTranslations.add(cacheKey);

 // Return original text on error
 return text;
}

export default translateText;

